import { useCallback, useMemo, useState } from 'react'

import { NetworkStatus, useMutation } from '@apollo/client'
import { ActionIcon, Menu, Switch, TextInput, Tooltip } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DnTable from 'src/components/Core/DnTable'
import AvatarGroup from 'src/components/DataDisplay/AvatarGroup/AvatarGroup'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_JOB_LISTING_TEMPLATES,
  UPDATE_JOB_LISTING_TEMPLATE,
  DELETE_JOB_LISTING_TEMPLATE,
} from 'src/graphql/jobListingTemplates.graphql'
import { EmployeeStatus } from 'src/graphql/types/employees'
import { JobListingTemplateStatus } from 'src/graphql/types/jobListingTemplates'
import IconCopy from 'src/icons/IconCopy'
import IconSearch from 'src/icons/IconSearch'
import IconThreeDots from 'src/icons/IconThreeDots'
import { formatStepType } from 'src/lib/formatters'

import JobListingTemplateDetailsDrawer from './components/JobListingTemplateDetailsDrawer'

const MAX_VISIBLE_STEPS = 5

const JobListingTemplatesTab = () => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  )
  const [opened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false)
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false)
  const [templateToDelete, setTemplateToDelete] = useState<any>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300)

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const {
    data: templatesData,
    loading: templatesLoading,
    networkStatus,
    refetch,
  } = useQuery(GET_JOB_LISTING_TEMPLATES, {
    variables: {
      search: debouncedSearchTerm || undefined,
    },
    notifyOnNetworkStatusChange: true,
    onError,
  })

  const [updateTemplate] = useMutation(UPDATE_JOB_LISTING_TEMPLATE, {
    onCompleted: () => {
      toast('Template updated successfully', 'success')
      refetch()
    },
    onError,
  })

  const [deleteTemplate] = useMutation(DELETE_JOB_LISTING_TEMPLATE, {
    onCompleted: () => {
      toast('Template deleted successfully', 'success')
      refetch()
    },
    onError,
  })

  const onStatusChange = useCallback(
    (template, checked) => {
      updateTemplate({
        variables: {
          id: template.jobListingTemplateId,
          input: {
            status: checked ? JobListingTemplateStatus.ACTIVE : JobListingTemplateStatus.INACTIVE,
          },
        },
      })
    },
    [updateTemplate]
  )

  const handleCopyId = useCallback((e, id) => {
    e.stopPropagation()
    navigator?.clipboard?.writeText?.(id)
    toast('Template ID copied to clipboard', 'success')
  }, [])

  const handleCreate = useCallback(() => {
    setSelectedTemplateId(null)
    openDrawer()
  }, [openDrawer])

  const handleClose = useCallback(() => {
    setSelectedTemplateId(null)
    closeDrawer()
  }, [closeDrawer])

  const handleRowClick = useCallback(
    (index, template) => {
      setSelectedTemplateId(template?.jobListingTemplateId)
      openDrawer()
    },
    [openDrawer]
  )

  const handleDeleteTemplate = useCallback(
    (template) => {
      setTemplateToDelete(template)
      openDeleteModal()
    },
    [openDeleteModal]
  )

  const confirmDeleteTemplate = useCallback(async () => {
    if (templateToDelete) {
      await deleteTemplate({
        variables: {
          id: templateToDelete.jobListingTemplateId,
        },
      })
      closeDeleteModal()
    }
  }, [templateToDelete, deleteTemplate, closeDeleteModal])

  const columns = useMemo(
    () => [
      {
        key: 'jobListingTemplateId',
        label: 'ID',
        width: 4,
        render: (template) => (
          <div className="flex items-center gap-2">
            <Tooltip label={template.jobListingTemplateId} withArrow>
              <ActionIcon
                variant="subtle"
                size={18}
                color="gray"
                onClick={(e) => handleCopyId(e, template.jobListingTemplateId)}
              >
                <IconCopy className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
              </ActionIcon>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'name',
        label: 'Template Name',
        width: 20,
        render: (template) => (
          <div
            key={`name-${template.jobListingTemplateId}`}
            className="truncate"
            title={template.name}
          >
            {template.name}
          </div>
        ),
      },
      {
        key: 'steps',
        label: 'Checklist',
        width: 33,
        render: (template) => {
          const allSteps = template.steps || []
          const sortedSteps = [...allSteps].sort((a, b) => a.order - b.order)
          const visibleSteps = sortedSteps.slice(0, MAX_VISIBLE_STEPS)
          const remainingCount = sortedSteps.length - visibleSteps.length
          const allStepNames = sortedSteps
            .map((s) => formatStepType(s.stepType, true))
            .join(', ')

          return (
            <Tooltip label={allStepNames} withArrow>
              <div className="flex flex-wrap gap-1">
                {visibleSteps.map((step) => (
                  <span
                    key={step.jobListingTemplateStepId}
                    className="rounded bg-doubleNickel-gray-100 px-1.5 py-0.5 text-xs text-doubleNickel-gray-700"
                  >
                    {formatStepType(step.stepType, true)}
                  </span>
                ))}
                {remainingCount > 0 && (
                  <span className="rounded bg-doubleNickel-gray-100 px-1.5 py-0.5 text-xs text-doubleNickel-gray-700">
                    +{remainingCount}
                  </span>
                )}
              </div>
            </Tooltip>
          )
        },
      },
      {
        key: 'createdBy',
        label: 'Created By',
        width: 13,
        render: (template) => (
          <div key={`createdBy-${template.jobListingTemplateId}`}>
            <AvatarGroup
              avatars={[
                {
                  firstName: template.createdBy?.firstName,
                  lastName: template.createdBy?.lastName,
                  isInactive:
                    template.createdBy?.status !== EmployeeStatus.ACTIVE,
                },
              ]}
            />
          </div>
        ),
      },
      {
        key: 'createdAt',
        label: 'Created At',
        width: 12,
        render: (template) => (
          <Tooltip
            label={dayjs(template.createdAt).format('MM/DD/YYYY h:mm A')}
            withArrow
          >
            <div key={`created-${template.jobListingTemplateId}`}>
              {dayjs(template.createdAt).format('MM/DD/YYYY')}
            </div>
          </Tooltip>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        width: 8,
        render: (template) => (
          <div
            key={`actions-${template.jobListingTemplateId}`}
            className="flex items-center justify-end gap-2"
          >
            <Tooltip
              label={
                template.status === JobListingTemplateStatus.ACTIVE
                  ? 'Disable template - Stop using this template for job listings'
                  : 'Enable template - Start using this template for job listings'
              }
              withArrow
            >
              <div onClick={(e) => e.stopPropagation()}>
                <Switch
                  checked={template.status === JobListingTemplateStatus.ACTIVE}
                  onChange={(event) =>
                    onStatusChange(template, event.currentTarget.checked)
                  }
                  size="sm"
                />
              </div>
            </Tooltip>
            <Menu shadow="md" position="bottom-end">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <IconThreeDots className="fill-doubleNickel-white stroke-doubleNickel-gray-600" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown className="w-40">
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteTemplate(template)
                  }}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        ),
      },
    ],
    [onStatusChange, handleCopyId, handleDeleteTemplate]
  )

  const templates = templatesData?.jobListingTemplates ?? []
  const tableLoading =
    templatesLoading || networkStatus === NetworkStatus.refetch

  return (
    <div className="h-full">
      <div className="flex h-full flex-col gap-4">
        <div className="flex flex-row justify-between">
          <div className="flex flex-col">
            <div className="text-lg font-semibold text-doubleNickel-gray-900">
              Job Listing Templates
            </div>
            <div className="text-sm text-doubleNickel-gray-600">
              {templates.length}{' '}
              {templates.length === 1 ? 'template' : 'templates'}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <TextInput
              placeholder="Search templates..."
              leftSection={
                <IconSearch className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              className="w-64"
            />
            <DNButton variant="primary" onClick={handleCreate}>
              Create Template
            </DNButton>
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <DnTable
            columns={columns}
            data={templates}
            enableColumnResize={false}
            loading={tableLoading}
            totalCount={templates.length}
            onRowClick={handleRowClick}
            emptyStateMessage="No templates found. Create one to get started."
          />
        </div>
      </div>
      {opened && (
        <JobListingTemplateDetailsDrawer
          opened={opened}
          close={handleClose}
          templateId={selectedTemplateId}
          refetch={refetch}
        />
      )}

      <DeleteModal
        title="Delete Template"
        description={
          templateToDelete ? (
            <div className="space-y-2">
              <p>Are you sure you want to delete this template?</p>
              <div className="rounded-md bg-doubleNickel-gray-50 p-3">
                <p className="break-all text-sm text-doubleNickel-gray-900">
                  {templateToDelete.name}
                </p>
              </div>
            </div>
          ) : (
            'This action cannot be undone.'
          )
        }
        opened={deleteModalOpened}
        close={closeDeleteModal}
        onDelete={confirmDeleteTemplate}
      />
    </div>
  )
}

export default JobListingTemplatesTab
